import React, { forwardRef } from 'react';
import { cn } from '../../../lib/cn';

/**
 * Stepper, wizard progress and order status.
 *
 * CSS: `desktop/system.css` `.dsk-stepper`, `.dsk-stepper__dot`,
 * `.dsk-stepper__seg`, with the `.is-done` and `.is-current` states.
 * Behaviour: none of the numbered reference behaviours.
 *
 * Anatomy is strictly dot, segment, dot, segment, dot: `.dsk-stepper__seg` is
 * `flex: 1`, so the segments do the spacing and the dots stay 12px. That is why
 * this is not a list of <li>s, which would put the connectors inside the items
 * and break the flex maths.
 *
 * ACCENT, not ink: a completed step reports progress, and progress is state.
 *
 * Accessibility: the dots are 12px circles with no text, so the progress is
 * carried entirely by colour. That would be invisible to a screen reader and to
 * anyone who cannot separate the two tones, so the whole strip is a single
 * `role="img"` with a sentence for a name, and the step labels are also
 * rendered as real text under it whenever `showLabels` is on.
 */
export const Stepper = forwardRef(function Stepper(
  { steps = [], current = 0, label, showLabels = false, className, ...rest },
  ref,
) {
  const names = steps.map((step) => (typeof step === 'string' ? step : step.label));

  const describedName =
    label ||
    (names.length
      ? `Step ${Math.min(current + 1, names.length)} of ${names.length}: ${names[Math.min(current, names.length - 1)]}`
      : 'Progress');

  return (
    <>
      <div
        ref={ref}
        className={cn('dsk-stepper', className)}
        role="img"
        aria-label={describedName}
        {...rest}
      >
        {names.map((name, index) => (
          <React.Fragment key={`${name}-${index}`}>
            {index > 0 ? (
              <span className={cn('dsk-stepper__seg', index <= current && 'is-done')} />
            ) : null}
            <span
              className={cn(
                'dsk-stepper__dot',
                index < current && 'is-done',
                index === current && 'is-current',
              )}
            />
          </React.Fragment>
        ))}
      </div>

      {showLabels ? (
        /* Layout only. The label row mirrors the dot row so each name sits
           under its dot; the end labels pull to the outer edges. */
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px' }}>
          {names.map((name, index) => (
            <span key={`label-${name}-${index}`} className="dsk-hint" aria-hidden="true">
              {name}
            </span>
          ))}
        </div>
      ) : null}
    </>
  );
});

export default Stepper;
